/**
 * Identity and lifetime of proactive calendar insight notifications
 * (deadline alerts, meeting confirmations, follow-up nudges and contact
 * suggestions).
 *
 * Identity comes from the structured source fields the producer stores with
 * the row (in `extraFields`), never from the copy: the copy is rewritten by a model on every
 * run, so a text-bearing key would mint a new notification each time. The
 * producer keys its rows with `calendarInsightIdentity`, and the feed read uses
 * the same function to fold any rows written before that (one row per run)
 * back into one.
 *
 * Lifetime: the producer stores `relevantUntil` (the deadline's day is over,
 * the meeting has ended), which the notification lifecycle honors for every
 * reader. Rows written before that carry only their anchors, so
 * `calendarInsightRelevantUntil` derives an end from those.
 */

import { isValidTimezone } from "@smartmanager/shell-ui/shared/time/timezone";

export const CALENDAR_INSIGHT_TYPES = [
  "deadline_alert",
  "follow_up_tracker",
  "contact_suggestion",
  "meeting_confirmed",
] as const;

/**
 * Insight types no producer writes any more. Their stored rows are dropped on
 * read; the producer also ends their retention on its next run.
 * `networking_suggestion` proposed introductions between strangers ("X could
 * meet Y") under raw addresses; contact suggestions replaced it.
 */
export const RETIRED_CALENDAR_INSIGHT_TYPES = ["networking_suggestion"] as const;

export type CalendarInsightType = (typeof CALENDAR_INSIGHT_TYPES)[number];

const DAY_MS = 24 * 60 * 60 * 1000;

type UnknownRecord = Record<string, unknown>;

const record = (value: unknown): UnknownRecord =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as UnknownRecord) : {};

const text = (value: unknown) => (typeof value === "string" ? value.trim() : "");

const finite = (value: unknown): number | null => {
  const parsed = typeof value === "number" ? value : typeof value === "string" && value.trim() ? Number(value) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const DATE_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;

const isCalendarInsightType = (value: string): value is CalendarInsightType =>
  (CALENDAR_INSIGHT_TYPES as readonly string[]).includes(value);

/**
 * The insight fields of a row. The notifications access module lifts the
 * stored `extraFields` blob onto the row it returns (stored columns winning),
 * so read the row itself, and fall back to a still-packed blob.
 */
const insightFields = (row: UnknownRecord): UnknownRecord => ({ ...record(row.extraFields), ...row });

/** The structured fields a calendar insight stores in `extraFields`. */
export type CalendarInsightFields = UnknownRecord & {
  proactiveInsightType?: unknown;
  eventId?: unknown;
  /** The deadline's calendar-store item, and whether it is a "todo" or an "event". */
  itemId?: unknown;
  itemType?: unknown;
  eventTitle?: unknown;
  deadlineTimestamp?: unknown;
  /** "YYYY-MM-DD" of an all-day deadline: the date the user sees, in no timezone. */
  dateKey?: unknown;
  /** "YYYY-MM-DD": the user's local day a deadline falls on, all-day or timed; the day its copy names. */
  dayKey?: unknown;
  /**
   * The zone `dayKey` was named in, stored only when it is a zone the user
   * set (never the UTC fallback). A timed deadline's day holds only there.
   */
  dayKeyZone?: unknown;
  threadId?: unknown;
  emailId?: unknown;
  /** Contact suggestion: the place the people were met, as `contactSourceKey` keys it. */
  sourceKey?: unknown;
  startTime?: unknown;
  endTime?: unknown;
  relevantUntil?: unknown;
};

/**
 * The stable identity of the underlying item, or null when the fields cannot
 * name one. One identity is one live notification.
 */
export function calendarInsightIdentity(type: string, fields: CalendarInsightFields): string | null {
  if (!isCalendarInsightType(type)) return null;
  switch (type) {
    case "deadline_alert": {
      // Todos and events share one calendar-store id space. Rows written before
      // deadlines carried their item type stored a todo's id as `eventId`, so
      // the `event:` key stays for every item: changing it would split one
      // deadline into two rows.
      const itemId = text(fields.itemId) || text(fields.eventId);
      if (itemId) return `deadline_alert:event:${itemId}`;
      // Without a source id, a real title on a date names the item. "Untitled"
      // does not: two such items on one day would fold into one.
      const title = text(fields.eventTitle).toLowerCase();
      if (!title || title === "untitled") return null;
      const dateKey = text(fields.dateKey);
      const at = finite(fields.deadlineTimestamp);
      const day = DATE_KEY_RE.test(dateKey) ? dateKey : at ? new Date(at).toISOString().slice(0, 10) : "";
      return day ? `deadline_alert:title:${title}:${day}` : null;
    }
    case "meeting_confirmed": {
      const eventId = text(fields.eventId);
      return eventId ? `meeting_confirmed:event:${eventId}` : null;
    }
    case "follow_up_tracker": {
      const thread = text(fields.threadId) || text(fields.emailId);
      return thread ? `follow_up_tracker:thread:${thread}` : null;
    }
    case "contact_suggestion": {
      // One suggestion per place the people were met.
      const sourceKey = text(fields.sourceKey).toLowerCase();
      return sourceKey ? `contact_suggestion:source:${sourceKey}` : null;
    }
  }
}

/** The calendar insight type of a stored row, or null for any other row. */
export function calendarInsightTypeOf(row: UnknownRecord): CalendarInsightType | null {
  const declared = text(insightFields(row).proactiveInsightType);
  return isCalendarInsightType(declared) ? declared : null;
}

/**
 * When the item a calendar insight row describes is over: a deadline once its
 * day has passed, a confirmed meeting once it has ended. Null for other rows
 * and for insights with no end (follow-up nudges, contact suggestions).
 *
 * Rows written before `relevantUntil` was stored fall back to their anchors.
 * Those rows anchored an all-day deadline at UTC midnight and never stored the
 * user's timezone, so the fallback ends it 24 hours later.
 */
export function calendarInsightRelevantUntil(row: UnknownRecord): number | null {
  const type = calendarInsightTypeOf(row);
  if (!type) return null;
  const fields = insightFields(row);
  const stored = finite(fields.relevantUntil);
  if (stored) return stored;
  if (type === "deadline_alert") {
    const at = finite(fields.deadlineTimestamp);
    return at ? at + DAY_MS : null;
  }
  if (type === "meeting_confirmed") {
    const start = finite(fields.startTime);
    const end = finite(fields.endTime);
    return end && start && end > start ? end : start;
  }
  return null;
}

/**
 * A day a row's copy names: the date ("YYYY-MM-DD") and the zone it was named
 * in. `timeZone` is null when no zone the user set named it: an all-day date
 * is the same date in every zone, so it is compared with the reader's today.
 */
export type NamedDay = { key: string; timeZone: string | null };

/** A stored zone the user set, or null. */
export function namedDayZone(value: unknown): string | null {
  const zone = text(value);
  return zone && isValidTimezone(zone) ? zone : null;
}

/**
 * The day a deadline row's copy names, or null. A timed deadline's day is
 * the day only in the zone that named it, so it counts only with that zone
 * (`dayKeyZone`): one named in the UTC fallback is a guess, and its readers
 * read the anchor in their own zone instead. An all-day deadline's date
 * (`dateKey`) is its day in any zone. Rows from before store no zone.
 */
export function calendarInsightNamedDay(row: UnknownRecord): NamedDay | null {
  if (calendarInsightTypeOf(row) !== "deadline_alert") return null;
  const fields = insightFields(row);
  const timeZone = namedDayZone(fields.dayKeyZone);
  const dayKey = text(fields.dayKey);
  if (timeZone && DATE_KEY_RE.test(dayKey)) return { key: dayKey, timeZone };
  const dateKey = text(fields.dateKey);
  return DATE_KEY_RE.test(dateKey) ? { key: dateKey, timeZone } : null;
}

/** True when a calendar insight row's item is over. False for every other row. */
export function isCalendarInsightOver(row: UnknownRecord, now: number): boolean {
  const until = calendarInsightRelevantUntil(row);
  return until !== null && until <= now;
}

/**
 * A deadline row written before deadlines carried their item type claimed
 * every item was a calendar event (so a todo offered "Open calendar") and held
 * itself in "now" as owed work. Which kind of item it named is lost, so the
 * read drops both claims: the row stays a heads-up about the date, keeps its
 * item id for identity, and offers only its suggested action.
 *
 * Self-retiring: the producer rewrites every deadline still 3 to 10 days out
 * on each run, so only rows for items already inside 3 days stay legacy, and
 * they end with their day, so none outlives the deploy by more than 10 days.
 * Safe to remove 10 days after this ships to prod.
 */
export function repairLegacyDeadlineRow<T extends UnknownRecord>(row: T): T {
  if (calendarInsightTypeOf(row) !== "deadline_alert") return row;
  const fields = insightFields(row);
  if (text(fields.itemType)) return row;
  const itemId = text(fields.itemId) || text(fields.eventId);
  const withoutEventClaim = (value: UnknownRecord): UnknownRecord => {
    const { eventId: _eventId, ...rest } = value;
    return { ...rest, ...(itemId ? { itemId } : {}) };
  };
  const {
    topPriorityEvent: _topPriorityEvent,
    sourceObjectType: _sourceObjectType,
    sourceObjectId: _sourceObjectId,
    ...rest
  } = withoutEventClaim(row);
  const nested = row.extraFields === undefined ? {} : { extraFields: withoutEventClaim(record(row.extraFields)) };
  return { ...rest, ...nested, actionRequired: false } as unknown as T;
}

const freshness = (row: UnknownRecord) =>
  finite(row.updatedDate) ?? finite(row.storedDate) ?? finite(row.createdDate) ?? 0;

/** True for a row of an insight type no producer writes any more. */
export function isRetiredCalendarInsightRow(row: UnknownRecord): boolean {
  return (RETIRED_CALENDAR_INSIGHT_TYPES as readonly string[]).includes(text(insightFields(row).proactiveInsightType));
}

/**
 * Read-time reconcile for calendar insight rows. Drops rows of retired types
 * and rows whose item is over, keeps only the freshest row per identity, and
 * repairs legacy deadline rows (`repairLegacyDeadlineRow`). Other rows pass
 * through untouched and in order. Pure and idempotent, so it cleans rows
 * persisted by earlier producers without a data write.
 */
export function reconcileCalendarInsightRows<T extends UnknownRecord>(rows: readonly T[], now: number): T[] {
  const live = rows.filter((row) => !isRetiredCalendarInsightRow(row));
  const winners = new Map<string, T>();
  for (const row of live) {
    const type = calendarInsightTypeOf(row);
    if (!type || isCalendarInsightOver(row, now)) continue;
    const identity = calendarInsightIdentity(type, insightFields(row));
    if (!identity) continue;
    const current = winners.get(identity);
    if (!current || freshness(row) > freshness(current)) winners.set(identity, row);
  }
  const kept = new Set<T>(winners.values());
  return live.filter((row) => {
    const type = calendarInsightTypeOf(row);
    if (!type) return true;
    if (kept.has(row)) return true;
    // A row with no derivable identity is kept unless its item is over.
    if (isCalendarInsightOver(row, now)) return false;
    return calendarInsightIdentity(type, insightFields(row)) === null;
  }).map(repairLegacyDeadlineRow);
}
